import Loader from '@ui/elements/Loader'
import FormRadioGroup from '@ui/forms/CheckboxGroup/FormRadioGroup'
import Input from '@ui/forms/Input'
import FormInput from '@ui/forms/Input/FormInput'
import InputsSection from '@ui/forms/InputsSection'
import FrameUtils from '@frame/FrameUtils'
import { StockItem, StockUtils } from '@frame/StockUtils'
import { SourcesDetails } from '@screens/frame/stock/StockFormUtils'
import { useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import useInventoryTrace from './useInventoryTrace'
import { StockForm } from './useStockForm'

export const StockFormSection = ({
  item,
  name,
  form,
  sources,
}: {
  item: StockItem
  sources: SourcesDetails | undefined
  name: 'frame' | 'temple'
  form: StockForm
}) => {
  const { t } = useTranslation(['frame', 'ui'])

  const { code, id, global, local, quantities } = useMemo(() => {
    const code = StockUtils.stockItemIsFrame(item)
      ? item.default_code
      : item.name

    const quantities = FrameUtils.getQty(item)

    return {
      code,
      id: item.id,
      quantities,
      global: {
        showQty: sources?.valid.global?.every((cfg) => cfg.qty !== 'ignore'),
        enabled: sources?.available.includes('global'),
      },
      local: {
        showQty: sources?.valid.local?.every((cfg) => cfg.qty !== 'ignore'),
        enabled: sources?.available.includes('local'),
      },
    }
  }, [item, sources])

  const source = useWatch({ control: form.control, name: `${name}.source` })
  const inventory = useWatch({
    control: form.control,
    name: `${name}.inventory`,
  })

  const trace = useInventoryTrace({ id, code, inventory, enabled: !!inventory })

  const { setValue, clearErrors } = form

  const text = 'frame:stock'

  useEffect(() => {
    // If we don't have a trace result, set it to undefined
    setValue(`${name}.traceResult`, trace.data?.result ?? undefined)
    // We need to validate whole form to trigger superRefine validation
    if (trace.data?.result != undefined) {
      form.trigger()
    }
  }, [trace.data, name, setValue, form])

  const localDesc = useMemo(() => {
    if (!local.enabled) {
      return t('ui:unavailable')
    }
    if (local.showQty) {
      return t('common:qty', { qty: quantities.store })
    }
    return t('ui:available')
  }, [local.showQty, quantities, t, local.enabled])

  const globalDesc = useMemo(() => {
    if (!global.enabled) {
      return t('ui:unavailable')
    }
    if (global.showQty) {
      return t('common:qty', { qty: quantities.remote })
    }
    return t('ui:available')
  }, [global.enabled, t, global.showQty, quantities])

  return (
    <>
      <InputsSection
        title={t(`${text}.source.header.title`)}
        desc={t(`${text}.source.header.desc`)}
      >
        <FormRadioGroup name={`${name}.source`} control={form.control}>
          <FormRadioGroup.Item
            id="global"
            disabled={!global.enabled}
            title={t(`${text}.source.options.global.label`)}
            desc={globalDesc}
            onPress={() => {
              clearErrors(`${name}.inventory`)
              setValue(`${name}.inventory`, '')
              setValue(`${name}.inventoryOption`, 'default')
            }}
          />
          <FormRadioGroup.Item
            id="local"
            disabled={!local.enabled}
            title={t(`${text}.source.options.local.label`)}
            desc={localDesc}
          />
        </FormRadioGroup>
      </InputsSection>
      <InputsSection
        title={t(`${text}.inventory.header.title`)}
        desc={t(`${text}.inventory.header.desc`)}
      >
        <FormRadioGroup
          defaultActive={['default']}
          name={`${name}.inventoryOption`}
          control={form.control}
        >
          <FormRadioGroup.Item
            id="default"
            onPress={() => {
              clearErrors(`${name}.inventory`)
              setValue(`${name}.inventory`, '')
            }}
            disabled={source !== 'local'}
            title={t(`${text}.inventory.options.default.label`)}
          >
            <Input className="w-[180px]" placeholder={code} disabled />
          </FormRadioGroup.Item>
          <FormRadioGroup.Item
            id="override"
            disabled={source !== 'local'}
            title={t(`${text}.inventory.options.override.label`)}
          >
            <FormInput
              hideIndicator={trace.isFetching}
              control={form.control}
              name={`${name}.inventory`}
              className="w-[180px]"
              disabled={source !== 'local'}
              successMessage={
                trace.data?.result === true
                  ? t(`validation:stock.inventory.success`)
                  : undefined
              }
              action={
                trace.isFetching && (
                  <View className="h-12 w-12 items-center justify-center">
                    <Loader loading />
                  </View>
                )
              }
            />
          </FormRadioGroup.Item>
        </FormRadioGroup>
      </InputsSection>
    </>
  )
}
